import React, { useEffect, useMemo, useState } from "react";
import styles from "./invoice_supplier_section.module.css";
import {
    Content,
    DateField,
    DatePicker,
    Dialog,
    ComboBox,
    DialogContainer,
    Flex,
    Heading,
    Item,
    Link,
    TextField,
    View,
} from "@adobe/react-spectrum";
import { useTranslation } from "react-i18next";
import { ListJournalRequest } from "../../../../../services/soap/finance/requests/ListJournalRequest";
import { useDependency } from "../../../../../contexts/DependencyProvider";

// ✅ React Spectrum date helpers
import { CalendarDate, parseDate } from "@internationalized/date";

// ✅ Codebook request
import { GetCodebookRequest } from "../../../../../services/soap/item/requests/GetCodebookRequest";

interface IInvoiceCustomAttributesSection {
    invoice: any;
    isEdit: any;
    onChange: (data: any) => void;
}

type Journal = any;

const InvoiceCustomAttributesSection: React.FC<IInvoiceCustomAttributesSection> = ({
    invoice,
    isEdit,
    onChange,
}) => {
    const [showMorefileds, setShowMorefields] = useState<boolean>(false);
    const { t } = useTranslation("invoice");
    const _t = (key: string) => t(`add_update_invoice.${key}`, { ns: "invoice" });

    const { store, financeService, itemService } = useDependency();
    const [journalList, setJournalList] = useState<Journal[]>([]);
    const [codebookMap, setCodebookMap] = useState<Record<string, any[]>>({}); // valuesId -> VALUES[]

    const handleShowMorefields = () => setShowMorefields(!showMorefileds);

    const getJournalList = async () => {
        const request = new ListJournalRequest(store.Server, store.SessionId);
        const response = await financeService.listJournal(request);
        setJournalList(response?.JOURNAL || []);
    };

    useEffect(() => {
        getJournalList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ✅ invoice.journal may be string id or object
    const selectedJournalId = useMemo(() => {
        const j = invoice?.journal;
        if (!j) return "";
        if (typeof j === "string") return j;
        if (typeof j === "object" && j.id) return j.id;
        return "";
    }, [invoice?.journal]);

    const selectedJournal = useMemo(() => {
        if (!selectedJournalId) return null;
        return journalList.find((j) => j.id === selectedJournalId) || null;
    }, [journalList, selectedJournalId]);

    // ✅ Build dynamic fields from selected journal: field0..field9
    const customFields = useMemo(() => {
        if (!selectedJournal) return [];
        const fields: Array<{
            index: number;
            name: string;
            type: number; // 1=text,2=int,3=float,4=date,5=list
            valuesId?: string;
            priority?: string;
        }> = [];

        for (let i = 0; i < 10; i++) {
            const name = selectedJournal[`field${i}Name`];
            const typeRaw = selectedJournal[`field${i}Type`];
            const valuesId = selectedJournal[`field${i}Values`];
            const priority = selectedJournal[`field${i}Priority`];

            const type = Number(typeRaw || 0);
            if (type !== 0 && name) {
                fields.push({ index: i, name, type, valuesId, priority });
            }
        }

        fields.sort((a, b) => Number(a.priority || 0) - Number(b.priority || 0));
        return fields;
    }, [selectedJournal]);

    // ✅ fetch codebooks for list fields (type=5)
    useEffect(() => {
        const fetchCodebooks = async () => {
            if (!showMorefileds) return;
            if (!selectedJournal) return;

            const listFields = customFields.filter((f) => f.type === 5 && f.valuesId);
            if (listFields.length === 0) return;

            for (const f of listFields) {
                const valuesId = String(f.valuesId || "");
                if (!valuesId) continue;
                if (codebookMap[valuesId]) continue;

                try {
                    const res = await itemService.getCodebook(
                        new GetCodebookRequest(store.Server, store.SessionId, valuesId)
                    );

                    // ✅ per your response: CODEBOOK[0].VALUES
                    const options = res?.CODEBOOK?.[0]?.VALUES || [];

                    setCodebookMap((prev) => ({
                        ...prev,
                        [valuesId]: Array.isArray(options) ? options : [],
                    }));
                } catch {
                    setCodebookMap((prev) => ({ ...prev, [valuesId]: [] }));
                }
            }
        };

        fetchCodebooks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showMorefileds, selectedJournalId, customFields]);

    // ✅ numeric filters
    const toIntegerString = (val: string) => val.replace(/[^\d-]/g, "");
    const toFloatString = (val: string) => {
        let v = val.replace(/[^\d\.\-]/g, "");
        const firstDot = v.indexOf(".");
        if (firstDot !== -1) {
            v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, "");
        }
        v = v.replace(/(?!^)-/g, "");
        return v;
    };

    // ✅ date parsing/formatting helpers
    const normalizeToYYYYMMDD = (val: any): string => {
        if (!val) return "";

        const s = String(val).trim();

        // ISO datetime => take date part
        if (s.includes("T")) {
            const datePart = s.split("T")[0];
            // assume YYYY-MM-DD
            return datePart;
        }

        // already YYYY-MM-DD
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

        // DD-MM-YYYY or DD/MM/YYYY
        const m = s.match(/^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})$/);
        if (m) {
            const dd = m[1].padStart(2, "0");
            const mm = m[2].padStart(2, "0");
            const yyyy = m[3];
            return `${yyyy}-${mm}-${dd}`;
        }

        return "";
    };

    const toCalendarDate = (val: any): CalendarDate | null => {
        const ymd = normalizeToYYYYMMDD(val);
        if (!ymd) return null;
        try {
            return parseDate(ymd);
        } catch {
            return null;
        }
    };

    // ✅ invoice field helpers (bind to invoice.field{n}, field{n}Id, field{n}Txt)
    const getInvoiceFieldKey = (index: number) => `field${index}`;
    const getInvoiceFieldIdKey = (index: number) => `field${index}Id`;
    const getInvoiceFieldTxtKey = (index: number) => `field${index}Txt`;

    const getFieldValue = (index: number) => invoice?.[getInvoiceFieldKey(index)] ?? "";
    const setFieldValue = (index: number, value: any) =>
        onChange({ ...invoice, [getInvoiceFieldKey(index)]: value });

    const getListIdValue = (index: number) => invoice?.[getInvoiceFieldIdKey(index)] ?? "";
    const getListTxtValue = (index: number) => invoice?.[getInvoiceFieldTxtKey(index)] ?? "";

    const setListValue = (index: number, id: any, txt: any) =>
        onChange({
            ...invoice,
            [getInvoiceFieldIdKey(index)]: id,
            [getInvoiceFieldTxtKey(index)]: txt,
        });

    // ✅ Summary: show displayed values (list uses Txt)
    const customAttributesSummary = useMemo(() => {
        if (!customFields.length) return "";

        const parts: string[] = [];

        for (const f of customFields) {
            if (f.type === 5) {
                const txt = getListTxtValue(f.index);
                if (txt && String(txt).trim() !== "") parts.push(String(txt).trim());
            } else {
                const v = getFieldValue(f.index);
                if (v !== undefined && v !== null && String(v).trim() !== "") parts.push(String(v).trim());
            }
        }

        return parts.join(", ");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoice, customFields]);

    // ✅ render one control
    const renderField = (f: any) => {
        // 1 = Text
        if (f.type === 1) {
            const v = getFieldValue(f.index);
            return (
                <TextField
                    label={f.name}
                    width={"100%"}
                    value={String(v ?? "")}
                    onChange={(val) => setFieldValue(f.index, val)}
                />
            );
        }

        // 2 = Integer
        if (f.type === 2) {
            const v = getFieldValue(f.index);
            return (
                <TextField
                    label={f.name}
                    width={"100%"}
                    inputMode="numeric"
                    value={String(v ?? "")}
                    onChange={(val) => setFieldValue(f.index, toIntegerString(val))}
                />
            );
        }

        // 3 = Float
        if (f.type === 3) {
            const v = getFieldValue(f.index);
            return (
                <TextField
                    label={f.name}
                    width={"100%"}
                    inputMode="decimal"
                    value={String(v ?? "")}
                    onChange={(val) => setFieldValue(f.index, toFloatString(val))}
                />
            );
        }

        // 4 = Date
        if (f.type === 4) {
            const raw = getFieldValue(f.index);
            const cd = toCalendarDate(raw);

            return (
                <DatePicker
                    label={f.name}
                    width={"100%"}
                    value={cd || undefined}
                    onChange={(val) => {
                        // store as YYYY-MM-DD (string)
                        setFieldValue(f.index, val ? val.toString() : "");
                    }}
                />
            );
        }

        // 5 = List
        if (f.type === 5) {
            const valuesId = f.valuesId ? String(f.valuesId) : "";
            const options = valuesId ? codebookMap[valuesId] || [] : [];
            const sorted = [...options].sort(
                (a: any, b: any) => Number(a?.priority || 0) - Number(b?.priority || 0)
            );

            const selectedId = getListIdValue(f.index);

            return (
                <ComboBox
                    label={f.name}
                    width={"100%"}
                    items={sorted}
                    selectedKey={selectedId || undefined}
                    onSelectionChange={(key) => {
                        const id = key ? String(key) : "";
                        const selected = sorted.find((x: any) => String(x?.id) === id);
                        const txt = selected?.name || "";
                        setListValue(f.index, id, txt);
                    }}
                    placeholder="Select..."
                    isDisabled={!valuesId}
                >
                    {(opt: any) => <Item key={opt.id}>{opt.name}</Item>}
                </ComboBox>
            );
        }

        // fallback
        const v = getFieldValue(f.index);
        return (
            <TextField
                label={f.name}
                width={"100%"}
                value={String(v ?? "")}
                onChange={(val) => setFieldValue(f.index, val)}
                description={`Unsupported field type: ${f.type}`}
            />
        );
    };

    function SupplierModal() {
        return (
            <Dialog
                width={{ L: "800px", base: "100%" }}
                aria-label="Custom Attributes"
                isDismissable
                onDismiss={() => setShowMorefields(false)}
            >
                <Heading marginStart={0} level={2}>
                    {_t(isEdit ? "custom_attributes" : "custom_attributes")}
                </Heading>

                <Content>
                    {!selectedJournalId ? (
                        <Flex marginTop={"size-200"}>
                            <TextField isReadOnly width={"100%"} label="Custom Attributes" value="Please select a journal first." />
                        </Flex>
                    ) : !selectedJournal ? (
                        <Flex marginTop={"size-200"}>
                            <TextField isReadOnly width={"100%"} label="Custom Attributes" value="Selected journal not found." />
                        </Flex>
                    ) : customFields.length === 0 ? (
                        <Flex marginTop={"size-200"}>
                            <TextField
                                isReadOnly
                                width={"100%"}
                                label="Custom Attributes"
                                value="This journal does not define any custom attributes."
                            />
                        </Flex>
                    ) : (
                        // ✅ 2 columns on L, 1 column on base
                        <Flex direction="column" gap={"size-150"} marginTop={"size-200"}>
                            {Array.from({ length: Math.ceil(customFields.length / 2) }).map((_, rowIndex) => {
                                const left = customFields[rowIndex * 2];
                                const right = customFields[rowIndex * 2 + 1];

                                // if odd last item -> make it full width on L
                                const isLastSingle = left && !right;

                                return (
                                    <Flex
                                        key={rowIndex}
                                        direction={{ base: "column", L: "row" }}
                                        gap={"size-150"}
                                        alignItems="start"
                                    >
                                        <View flex>{left ? renderField(left) : null}</View>

                                        {isLastSingle ? null : <View flex>{right ? renderField(right) : null}</View>}
                                    </Flex>
                                );
                            })}
                        </Flex>
                    )}
                </Content>
            </Dialog>
        );
    }

    return (
        <Flex direction={{ base: "column", L: "column" }} UNSAFE_className={styles.main_container}>
            <Flex justifyContent={"end"}>
                <Link UNSAFE_className={styles.show_more_button} onPress={handleShowMorefields} isQuiet>
                    Show more
                </Link>
            </Flex>

            <Flex>
                <TextField isReadOnly label="Custom Attributes" width={"100%"} value={customAttributesSummary} />
            </Flex>

            <DialogContainer onDismiss={() => setShowMorefields(false)}>
                {showMorefileds && SupplierModal()}
            </DialogContainer>
        </Flex>
    );
};

export default InvoiceCustomAttributesSection;
